/**
 * 航段费用 Hub 页面
 *
 * 将原本 4 个独立菜单页合并为单菜单 + 4 Tab 切换：
 *   - 航段费用查询（leg-cost）        权限：settlement:read
 *   - 公共费用（cost-allocation）     权限：settlement:allocate
 *   - ACMI 录入（acmi）               权限：settlement:acmi
 *   - ACMI 调整（acmi-adjustment）    权限：settlement:acmi
 *   - Commission 录入（commission）   权限：settlement:commission
 *
 * 设计要点：
 *   - Tab 与 URL ?tab=xxx 双向同步，便于深链接和刷新保持
 *   - 无权限的 Tab 不渲染（动态过滤 items）
 *   - URL 中 tab 参数若不存在或无权限，自动 fallback 到首个可见 Tab
 *   - 关键：未激活 Tab 仅 CSS 隐藏（保持挂载），切走再切回来内部状态保留，
 *     用户在 ACMI/Commission/公共费用 录入到一半切走不会丢数据
 *   - 不引入新的 page-container，外层只渲染 Tabs，复用各子页原有的页面结构
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tabs, Empty, Badge, Space } from "antd";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import { LegCostManagementPage } from "../LegCostManagement";
import { CostAllocationPage } from "../CostAllocation";
import { AcmiEntryPage } from "../AcmiEntry";
import { AcmiAdjustmentPage } from "../AcmiAdjustment";
import { CommissionEntryPage } from "../CommissionEntry";
import DataAnomalyTab from "./components/DataAnomalyTab";

const TAB_KEYS = {
    LEG_COST: "leg-cost",
    COST_ALLOCATION: "cost-allocation",
    ACMI: "acmi",
    ACMI_ADJUSTMENT: "acmi-adjustment",
    COMMISSION: "commission",
    DATA_ANOMALY: "data-anomaly",
};

const LegCostHubPage = () => {
    const { hasPermission, loading: authLoading } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get("tab");

    const switchTab = useCallback(
        (key) => {
            setSearchParams({ tab: key });
        },
        [setSearchParams],
    );

    // S3 — 数据异常 Tab 角标用的"高紧迫度待处理"计数,由子组件 onCountsChange 回传
    const [anomalyCountsBySeverity, setAnomalyCountsBySeverity] = useState({});
    const handleAnomalyCountsChange = useCallback((countsBySeverity) => {
        setAnomalyCountsBySeverity(countsBySeverity || {});
    }, []);

    // 按权限动态生成 Tab 列表
    const tabItems = useMemo(() => {
        const items = [];

        if (hasPermission("settlement:read")) {
            items.push({
                key: TAB_KEYS.LEG_COST,
                label: "航段费用查询",
                children: <LegCostManagementPage />,
            });
        }
        if (hasPermission("settlement:allocate")) {
            items.push({
                key: TAB_KEYS.COST_ALLOCATION,
                label: "公共费用",
                children: <CostAllocationPage />,
            });
        }
        if (hasPermission("settlement:acmi")) {
            items.push({
                key: TAB_KEYS.ACMI,
                label: "ACMI 录入",
                children: <AcmiEntryPage />,
            });
            items.push({
                key: TAB_KEYS.ACMI_ADJUSTMENT,
                label: "ACMI 调整",
                children: <AcmiAdjustmentPage />,
            });
        }
        if (hasPermission("settlement:commission")) {
            items.push({
                key: TAB_KEYS.COMMISSION,
                label: "Commission 录入",
                children: <CommissionEntryPage />,
            });
        }
        // S3 第 5 Tab — settlement:exception:read 可见(T39 / T44 只读模式)
        if (hasPermission("settlement:exception:read")) {
            const highCount = anomalyCountsBySeverity.HIGH || 0;
            items.push({
                key: TAB_KEYS.DATA_ANOMALY,
                label: (
                    <Space size={6}>
                        <span>数据异常</span>
                        {highCount > 0 && (
                            <Badge
                                count={highCount}
                                style={{ backgroundColor: "#ff4d4f" }}
                                overflowCount={99}
                            />
                        )}
                    </Space>
                ),
                // 角标数字靠子组件加载数据后回传;Tabs 默认懒渲染未激活面板,
                // 不 forceRender 的话用户切进来之前角标永远是空的,提醒机制失效
                forceRender: true,
                children: <DataAnomalyTab onCountsChange={handleAnomalyCountsChange} />,
            });
        }

        return items;
    }, [hasPermission, anomalyCountsBySeverity, handleAnomalyCountsChange]);

    const allowedKeys = useMemo(() => tabItems.map((t) => t.key), [tabItems]);

    // 当前激活的 Tab：URL 中的 tab 参数若无效则 fallback 到首个可见 Tab
    const activeKey = allowedKeys.includes(tabFromUrl) ? tabFromUrl : allowedKeys[0];

    // URL 与实际 activeKey 不一致时同步回写（用 replace 避免污染历史栈）
    // 仅依赖标量 tabFromUrl，避免 searchParams 引用每次 render 都变化造成 effect 频繁调度
    useEffect(() => {
        if (!activeKey) return;
        if (tabFromUrl !== activeKey) {
            setSearchParams({ tab: activeKey }, { replace: true });
        }
    }, [activeKey, tabFromUrl, setSearchParams]);

    if (authLoading) return null;

    if (tabItems.length === 0) {
        return (
            <div className="page-container">
                <Empty description="您没有访问任何航段费用相关功能的权限" />
            </div>
        );
    }

    return (
        <Tabs
            type="card"
            size="large"
            activeKey={activeKey}
            onChange={switchTab}
            items={tabItems}
            tabBarStyle={{ paddingLeft: 16, paddingRight: 16, paddingTop: 12, marginBottom: 0 }}
        />
    );
};

export default LegCostHubPage;
